import React, { Fragment, useMemo, useState } from 'react';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import AppTable from '../../../../constants/AppTable';
import { FiscalYear } from '../../../../helpers/models/Models';
import { StatusBadge } from '../../../../constants/AppBadge';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { useFormik } from 'formik';
import { putDataToTheServer } from '../../../../helpers/api';
import { handleChangeDate } from '../../../../helpers/handleChange';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';

interface FiscalYearListViewProps {
    results: {
        results: FiscalYear[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const FiscalYearListView: React.FC<FiscalYearListViewProps> = ({ handleFormSuccess, results, loading, error, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<FiscalYear | null>(null);

    const columns = [
        { header: 'SN', accessor: 'fiscal_year_id', sortable: true },
        { header: 'Fiscal Year Name', accessor: 'fiscal_year_name', sortable: true },
        { header: 'From Date', accessor: 'from_date_n', sortable: false },
        { header: 'To Date', accessor: 'to_date_n', sortable: false },
        { header: 'Current Fiscal', accessor: 'is_current', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];

    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: FiscalYear) => ({
            fiscal_year_id: item.fiscal_year_id,
            fiscal_year_name: item.fiscal_year_name,
            from_date_n: item.from_date_n,
            to_date_n: item.to_date_n,
            status: item.status ? 1 : 2,  // Assuming status is boolean
            is_current_fiscal: item.is_current_fiscal ===1 ? 1 : 2,  // Convert to number
            is_current: <StatusBadge status={item.is_current_fiscal ? 1 : 0} />,
            active: item.status ? 1 : 0,
            actions: (
                <button onClick={() => handleEdit(item)}>Edit</button>
            ),
        }));
    }, [results]);
    
    

    const handleEdit = (item: FiscalYear) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };
    const formik = useFormik({
        initialValues: {
            fiscal_year_name: currentItem?.fiscal_year_name || "",
            from_date_n: currentItem?.from_date_n || "",
            to_date_n: currentItem?.to_date_n || "",
            is_current_fiscal: currentItem?.is_current_fiscal=== 1 ? 1 : 2,
            status: currentItem?.status ===  1 ? 1 : 2, // Initialize as a string for form handling
        },
        // Uncomment and use validationSchema as needed
        // validationSchema: toleValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("fiscal_year_name", values.fiscal_year_name);
            formData.append("from_date_n", values.from_date_n);
            formData.append("to_date_n", values.to_date_n);
            formData.append("is_current_fiscal", values.is_current_fiscal?.toString()=== "1" ? "1" : "0");
            formData.append("status", values.status?.toString() === "1" ? "1" : "0"); // Convert string to number for API
            try {
                await putDataToTheServer(AppURL.AccountURL + 'account/fiscal/year/retrieve/update/view/' + currentItem?.fiscal_year_id, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    } else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });


    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }


    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Tole Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>

                                <InputTextBox
                                    type='text'
                                    name='fiscal_year_name'
                                    label='Fiscal Year Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.fiscal_year_name}
                                    error={formik.errors.fiscal_year_name}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.fiscal_year_name}
                                    required={true}
                                    placeholder='Enter Fiscal Year Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='from_date_n'
                                    label='From Date'
                                    handleChange={(e) => handleChangeDate(e, { setFieldValue: formik.setFieldValue, name: 'from_date_n' })}
                                    value={formik.values.from_date_n}
                                    error={formik.errors.from_date_n}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.from_date_n}
                                    required={true}
                                    placeholder='XXXX/XX/XX'
                                />

                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='to_date_n'
                                    label='To Date'
                                    handleChange={(e) => handleChangeDate(e, { setFieldValue: formik.setFieldValue, name: 'to_date_n' })}
                                    value={formik.values.to_date_n}
                                    error={formik.errors.to_date_n}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.to_date_n}
                                    required={true}
                                    placeholder='XXXX/XX/XX'
                                />

                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="is_current_fiscal"
                                    label='Current Fiscal'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.is_current_fiscal}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    placeholder="Select Options"
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="status"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    placeholder="Select Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default FiscalYearListView;
